import parse from "html-react-parser";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { ActionCreatorWithoutPayload } from "@reduxjs/toolkit";
import escapeImage from "assets/icon-escape.svg";
import energyImage from "assets/stat-energy.svg";
import firepowerImage from "assets/stat-firepower.svg";
import precisionImage from "assets/stat-precision.svg";
import resilienceImage from "assets/stat-resilience.svg";
import speedImage from "assets/stat-speed.svg";
import SkillIcon from "components/base/SkillIcon";
import Tooltip, { TooltipDesc, TooltipName } from "components/base/Tooltip";
import { media } from "components/styles/breakpoints";
import { getEscapeChance, getHitChance } from "libs/fight";
import { getStatRangeData } from "libs/stats";
import {
  activateSkill,
  escapeFight,
  setLastFightAction,
  showMessage,
  targetReactor,
  targetShields,
  targetTargetingComputer,
  targetThrusters,
  targetWeapons,
} from "redux/actions";
import {
  getDerivedStatRanges,
  getDerivedStats,
  getPilotSkills,
} from "redux/selectors/characterSelectors";
import {
  getCharacterFighterData,
  getFightTurnsInfo,
  getLastFightAction,
  getOpponentBaseInfo,
  getOpponentDerivedStats,
  getOpponentFighterData,
  getSkillsFightInfo,
} from "redux/selectors/fightSelectors";
import { FightActionTypes, SkillModel } from "types";
import { COLORS } from "utils/constants";
import { formatPercentage } from "utils/formatters";

interface ActionProps {
  $isLastAction: boolean;
  $isTurnDisabled: boolean;
  $isActionDisabled?: boolean;
}

interface PartIconProps {
  image: string;
  color: string;
}

interface EscapeIconProps {
  image: string;
}

interface TooltipAttackDescProps {
  color: string;
}

const FightActionsWrapper = styled.div`
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  opacity: 1;
`;

const FightActionGroups = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const lastActionMixin = css`
  animation: pulse 0.5s ease-out;
  transition-delay: 500ms;

  @keyframes pulse {
    0% {
      transform: translateY(0);
      filter: saturate(100%) brightness(100%);
    }
    40% {
      transform: translateY(-40%);
      filter: saturate(150%) brightness(150%);
    }
    80% {
      transform: translateY(-40%);
      filter: saturate(150%) brightness(150%);
    }
    100% {
      transform: translateY(0);
      filter: saturate(100%) brightness(100%);
    }
  }
`;

const actionDisabledMixin = css`
  opacity: 0.5;
  cursor: auto;
  filter: saturate(0);
`;

const turnDisabledsMixin = css`
  opacity: 0.4;
  pointer-events: none;
`;

const Action = styled.div<ActionProps>`
  transition: opacity 200ms ease-out;
  pointer-events: auto;
  ${(props) => props.$isLastAction && lastActionMixin}
  ${(props) => props.$isTurnDisabled && turnDisabledsMixin}
  ${(props) => props.$isActionDisabled && actionDisabledMixin}
`;

// Regular Attacks

const AttackAction = styled(Action)`
  background-color: #0c434c70;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;

  ${media.desktop`
    padding: 10px;
  `}

  &:hover {
    background-color: #0c434c;
  }

  ${(props) => props.$isLastAction && lastActionMixin}
`;

const PartIcon = styled.div<PartIconProps>`
  height: 16px;
  width: 16px;
  background-color: ${(props) => props.color};
  mask: url(${(props) => props.image}) no-repeat center;

  ${media.desktop`
    height: 24px;
    width: 24px;
  `}
`;

// Skills

const SkillAction = styled(Action)`
  position: relative;
  width: 32px;
  aspect-ratio: 1/1;
  font-size: 18px;

  ${media.desktop`
    width: 44px;
    font-size: 24px;
  `}
`;

// Tactics

const EscapeAction = styled(Action)`
  background-color: #d8000099;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #d80000;
  }

  ${media.desktop`
    padding: 8px;
  `}
`;

const EscapeIcon = styled.div<EscapeIconProps>`
  height: 20px;
  width: 20px;
  background-color: #000000;
  mask: url(${(props) => props.image}) no-repeat center;

  ${media.desktop`
    height: 28px;
    width: 28px;
  `}
`;

const KeyCommand = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 2px;
  border-top-left-radius: 4px;
  border-bottom-right-radius: 4px;
  backdrop-filter: blur(3px);
  text-shadow: 0 1px 1px #000000;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  z-index: 2;
  color: white;
  pointer-events: none;
  opacity: 0;

  ${media.desktop`
    opacity: 1;
  `}
`;

const TooltipAttackDesc = styled(TooltipDesc)<TooltipAttackDescProps>`
  span {
    color: ${(props) => props.color};
  }

  strong {
    color: color-mix(in srgb, ${COLORS.FIREPOWER}, ${COLORS.PRECISION});
  }
`;

const TooltipHitChanceDesc = styled(TooltipDesc)`
  strong {
    color: ${COLORS.PRECISION};
  }
`;

const TooltipFleeDesc = styled(TooltipDesc)`
  strong {
    color: ${COLORS.SPEED};
  }
`;

interface TargetPart {
  name: string;
  stat: string;
  image: string;
  color: string;
  action: ActionCreatorWithoutPayload<string>;
}

const TARGET_PARTS: TargetPart[] = [
  {
    name: "weapons",
    stat: "firepower",
    image: firepowerImage,
    color: COLORS.FIREPOWER,
    action: targetWeapons,
  },
  {
    name: "shields",
    stat: "resilience",
    image: resilienceImage,
    color: COLORS.RESILIENCE,
    action: targetShields,
  },
  {
    name: "thrusters",
    stat: "speed",
    image: speedImage,
    color: COLORS.SPEED,
    action: targetThrusters,
  },
  {
    name: "targeting grid",
    stat: "precision",
    image: precisionImage,
    color: COLORS.PRECISION,
    action: targetTargetingComputer,
  },
  {
    name: "reactor core",
    stat: "antimatter",
    image: energyImage,
    color: COLORS.ENERGY,
    action: targetReactor,
  },
];

export default function FightActions() {
  const dispatch = useDispatch();

  // Character data
  const characterFighter = useSelector(getCharacterFighterData);
  const skills = useSelector(getPilotSkills);
  const derivedStats = useSelector(getDerivedStats);
  const derivedStatRanges = useSelector(getDerivedStatRanges);
  const { skillsRecharge } = useSelector(getSkillsFightInfo);
  const characterStats = derivedStats.current.complete;
  const lastFightAction = useSelector(getLastFightAction);

  // Opponent data
  const { areActionsEnabled } = useSelector(getFightTurnsInfo);
  const { name: opponentName } = useSelector(getOpponentBaseInfo);
  const opponentFighter = useSelector(getOpponentFighterData);
  const opponentDerivedStats = useSelector(getOpponentDerivedStats);
  const opponentStats = opponentDerivedStats.current.complete;

  const trainedSkills = skills.filter((skill) => !!skill.isTrained);

  const hitChance = getHitChance(
    characterStats.attackAccuracy,
    opponentStats.attackEvasion
  );
  const escapeChance = getEscapeChance(
    characterStats.movementSpeed,
    opponentStats.movementSpeed
  );

  const attackRangeInfo = getStatRangeData("attackDamageRange");
  const weakenRangeInfo = getStatRangeData("weakenPartsRange");
  const { attackDamageRange, weakenPartsRange } =
    derivedStatRanges.current.complete;

  const handleAttackAction = (part: TargetPart, index: number) => {
    dispatch(setLastFightAction({ type: "attack", index }));
    dispatch(part.action());
  };

  const handleSkillAction = (
    skill: SkillModel,
    isRecharging: boolean,
    index: number
  ) => {
    if (isRecharging) {
      dispatch(showMessage(`${skill.name} is still recharging`));
    } else {
      dispatch(setLastFightAction({ type: "skill", index }));
      dispatch(activateSkill(skill.slug));
    }
  };

  const handleEscapeAction = () => {
    dispatch(setLastFightAction({ type: "tactic", index: 0 }));
    dispatch(escapeFight());
  };

  const isLastAction = (type: FightActionTypes, index: number) => {
    return lastFightAction?.type === type && lastFightAction.index === index;
  };

  return (
    <FightActionsWrapper>
      <FightActionGroups>
        <ActionGroup>
          {TARGET_PARTS.map((part, index) => (
            <Tooltip
              key={part.name}
              color={part.color}
              content={
                <>
                  <TooltipName>Attack: {part.name}</TooltipName>
                  <TooltipAttackDesc color={part.color}>
                    {parse(`Inflict <strong>${attackRangeInfo.formatter(
                      attackDamageRange.min,
                      attackDamageRange.max
                    )}</strong>
                   ship damage and weaken <span>${part.stat}</span> by 
                  <strong>${weakenRangeInfo.formatter(
                    weakenPartsRange.min,
                    weakenPartsRange.max
                  )}</strong>
                   by targeting ${opponentName}'s ${part.name}. `)}
                  </TooltipAttackDesc>
                  <TooltipHitChanceDesc>
                    {parse(`Based on the attacker's accuracy vs. the defender's evasion, the current chance of successfully landing this attack is 
                  <strong>${formatPercentage(hitChance)}</strong>.`)}
                  </TooltipHitChanceDesc>
                </>
              }
            >
              <AttackAction
                $isTurnDisabled={!areActionsEnabled}
                $isLastAction={isLastAction("attack", index)}
                onClick={() => handleAttackAction(part, index)}
              >
                <KeyCommand>{index + 1}</KeyCommand>
                <PartIcon image={part.image} color={part.color} />
              </AttackAction>
            </Tooltip>
          ))}
        </ActionGroup>
        <ActionGroup>
          {trainedSkills.map((skill, index) => {
            const rechargeTurns = skillsRecharge[skill.slug] || 0;
            const totalRecharge = skill.recharge || rechargeTurns;
            const rechargePercentage = (rechargeTurns / totalRecharge) * 100;
            const isRecharging = rechargeTurns > 0;

            return (
              <SkillAction
                key={skill.slug}
                $isLastAction={isLastAction("skill", index)}
                $isTurnDisabled={!areActionsEnabled}
                $isActionDisabled={isRecharging}
                onClick={() => handleSkillAction(skill, isRecharging, index)}
              >
                <KeyCommand>⇧{index + 1}</KeyCommand>
                <SkillIcon
                  skill={skill}
                  attacker={characterFighter}
                  defender={opponentFighter}
                  isShowValues
                  isRecharging={isRecharging}
                  rechargeTurns={rechargeTurns}
                  rechargePercentage={rechargePercentage}
                />
              </SkillAction>
            );
          })}
        </ActionGroup>
        <ActionGroup>
          <Tooltip
            color="#d80000"
            content={
              <>
                <TooltipName>Escape</TooltipName>
                <TooltipDesc>
                  Attempt to flee the battle, with your chances of success
                  determined by your ship's movement speed relative to the
                  opponent's.
                </TooltipDesc>
                <TooltipDesc>
                  <TooltipFleeDesc>
                    {parse(`Your current chance of escape is 
                  <strong>${formatPercentage(escapeChance)}</strong>.`)}
                  </TooltipFleeDesc>
                </TooltipDesc>
              </>
            }
          >
            <EscapeAction
              $isLastAction={isLastAction("tactic", 0)}
              $isTurnDisabled={!areActionsEnabled}
              onClick={() => handleEscapeAction()}
            >
              <KeyCommand>ESC</KeyCommand>
              <EscapeIcon image={escapeImage} />
            </EscapeAction>
          </Tooltip>
        </ActionGroup>
      </FightActionGroups>
    </FightActionsWrapper>
  );
}
